import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { toCamel, toSnake } from 'convert-keys';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PagesService, Type } from 'pages-loader/dist/pages-loader';
import { CookieService } from './cookie.service';

@Injectable({ providedIn: 'root' })
export class GenericOldService<T> {
  apiLocation: string;
  hierarchyService: PagesService;

  constructor(
    private httpClient: HttpClient,
    private cookieService: CookieService,
  ) {
    this.apiLocation = this.cookieService.getMarketsMock() ? 'http://wiremock:8888/v1' : '/api/v1';

    this.hierarchyService = new PagesService(
      !!this.cookieService.getMarketsMock(),
      this.cookieService.getDesSession(),
      this.httpClient,
    );
  }

  get(path: string, params?: HttpParams, mappingCallback?: (response: unknown) => T): Observable<T> {
    return this.httpClient
      .get<T>(`${this.apiLocation}/${path}`, { params })
      .pipe(map(item => this.mapResponse(item, mappingCallback)));
  }

  getById(
    path: string,
    id: string | number,
    params?: HttpParams,
    mappingCallback?: (response: unknown) => T,
  ): Observable<T> {
    return this.httpClient
      .get<T>(`${this.apiLocation}/${path}/${id}`, { params })
      .pipe(map(item => this.mapResponse(item, mappingCallback)));
  }

  getList(path: string, params?: HttpParams, mappingCallback?: (response: unknown) => T): Observable<T[]> {
    return this.httpClient.get<T[]>(`${this.apiLocation}/${path}`, { params }).pipe(
      map(items => {
        const data = items['data'];
        if (!mappingCallback) {
          return data.map(p => toCamel<T>(p)) as T[];
        }
        return data.map(p => toCamel<T>(mappingCallback(p))) as T[];
      }),
    );
  }

  getTree(types: string[]): Promise<Type[]> {
    return this.hierarchyService.getSpecificHierarchies(types);
  }

  getPaginated(path: string, index: number, page: number, params?: HttpParams): Observable<T[]> {
    const listParams = new HttpParams().set('limit', index.toString()).set('offset', page.toString());

    return this.httpClient
      .get<T[]>(`${this.apiLocation}/${path}?${listParams.toString()}`, {
        params,
      })
      .pipe(map(list => list['data'].map(p => toCamel<T>(p))));
  }

  add(
    path: string,
    resource: T | Partial<T>,
    params?: HttpParams,
    mappingCallback?: (response: unknown) => T,
  ): Observable<T> {
    return this.httpClient
      .post<T>(`${this.apiLocation}/${path}`, toSnake(resource), {
        params,
      })
      .pipe(map(item => this.mapResponse(item, mappingCallback)));
  }

  update(
    path: string,
    id: string | number,
    resource: T | Partial<T> | T[] | Partial<T>[],
    params?: HttpParams,
    mappingCallback?: (response: unknown) => T,
  ): Observable<T> {
    return this.httpClient
      .put<T>(`${this.apiLocation}/${path}/${id}`, toSnake(resource), {
        params,
      })
      .pipe(map(item => this.mapResponse(item, mappingCallback)));
  }

  delete(
    path: string,
    id: string | number,
    params?: HttpParams,
    mappingCallback?: (response: unknown) => T,
  ): Observable<T> {
    return this.httpClient
      .delete<T>(`${this.apiLocation}/${path}/${id}`, { params })
      .pipe(map(item => this.mapResponse(item, mappingCallback)));
  }

  private mapResponse(item, mappingCallback): T {
    const data = item['data'];
    if (!mappingCallback) {
      if (typeof data !== 'object') {
        return data;
      }
      return toCamel<T>(data);
    }
    return toCamel<T>(mappingCallback(data));
  }
}
